import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { apiConfigResolver } from '@df/api/config/api-config.resolver';
import { basketItemsStockReloadResolver } from '@df/basket/stock-reload.resolver';
import { RouteResolver } from '@df/catalog/route/route.resolver';
import { storesResolver } from '@df/catalog/store/stores.resolver';
import { CheckoutSuccessResolver } from '@df/checkout/checkout-success.resolver';
import { SearchResolver } from '@df/search/search.resolver';
import { CustomerNotLoggedGuard } from '@df/session/customer/customer-not-logged.guard';
import { CustomerReloadResolver } from '@df/session/customer/customer-reload.resolver';
import { CustomerResolver } from '@df/session/customer/customer.resolver';
import { WishlistLoadResolver } from '@df/session/wishlist/wishlist-load.resolver';
import { DevOnlyGuard } from '@df/ui/dev-only.guard';
import { PhotoswipeService } from '@df/ui/photoswipe/photoswipe.service';
import { AppRootComponent } from './app-root.component';
import { CatalogViewComponent } from './catalog/ui/catalog-view.component';
import { CheckoutSuccessComponent } from './checkout/checkout-success.component';
import { LoginFormComponent } from './login/form/login-form.component';
import { LoginViewComponent } from './login/login-view.component';
import { LoginResetPasswordComponent } from './login/reset-password/login-reset-password.component';
import { SearchViewComponent } from './search/view/search-view.component';
import { PlainRouterOutletComponent } from './util/plain-router-outlet.component';

export const appRoutes: Route[] = [
    {
        path: '_library',
        canActivate: [DevOnlyGuard],
        resolve: {
            appData: apiConfigResolver
        },
        loadChildren: () =>
            import(
                /* webpackChunkName: "dfd.aw" */
                './module/df-demo/dfd.module'
            ).then(m => m.DfdModule)
    },
    {
        path: '_icmslibrary',
        canActivate: [DevOnlyGuard],
        resolve: {
            appData: apiConfigResolver
        },
        loadChildren: () =>
            import(
                /* webpackChunkName: "icmsdfd.aw" */
                '@icms/ui/icms-ui-demo.module'
            ).then(m => m.ICmsUiDemoModule)
    },
    {
        path: '',
        component: AppRootComponent,
        resolve: {
            appData: apiConfigResolver
        },
        children: [
            {
                path: 'account',
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "account-routes.async" */
                        './account/account.routes'
                    ).then(r => r.accountRoutes)
            },
            {
                path: 'wishlist',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-wishlist.async" */
                        './account/wishlist/account-wishlist.component'
                    ).then(c => c.AccountWishlistComponent),
                resolve: {
                    wishlist: WishlistLoadResolver
                }
            },
            {
                path: 'login',
                component: LoginViewComponent,
                canActivate: [CustomerNotLoggedGuard],
                children: [
                    {
                        path: '',
                        component: LoginFormComponent
                    },
                    {
                        path: 'reset-password',
                        component: LoginResetPasswordComponent
                    }
                ]
            },
            {
                path: 'basket',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "basket-view.async" */
                        './basket/basket-view.component'
                    ).then(c => c.BasketViewComponent),
                resolve: {
                    customer: CustomerReloadResolver,
                    stock: basketItemsStockReloadResolver
                }
            },
            {
                path: 'checkout',
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "checkout-routes.async" */
                        './checkout/checkout.routes'
                    ).then(c => c.checkoutRoutes)
            },
            {
                path: 'checkout/success',
                component: CheckoutSuccessComponent,
                resolve: {
                    customer: CustomerResolver,
                    checkoutSuccessData: CheckoutSuccessResolver
                }
            },
            {
                path: 'search',
                component: PlainRouterOutletComponent,
                children: [
                    {
                        path: '**',
                        component: SearchViewComponent,
                        resolve: {
                            searchResult: SearchResolver
                        }
                    }
                ]
            },
            {
                path: 'error',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "error-500.aw" */
                        './catalog/ui/error-500.component'
                    ).then(m => m.Error500Component)
            },
            {
                path: 'spcp/switch',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "sagepay-cloud-payment.async" */
                        './module/sagepay/cloud-payment/sagepay-cloud-payment.component'
                    ).then(m => m.SagepayCloudPaymentComponent),
                resolve: {
                    allStores: storesResolver,
                    customer: CustomerResolver
                }
            },
            {
                path: '**',
                component: CatalogViewComponent,
                resolve: {
                    route: RouteResolver
                },
                canDeactivate: [() => inject(PhotoswipeService).canDeactivate()],
                data: {
                    preload: true
                }
            }
        ]
    }
];
